import { type FC, useCallback, useMemo } from "react";
import notification from "@app/services/notification";
import { useScannerRegistration, type ScannerHintConfig, type ScannerRegistrationConfig } from "@app/scanner";
import type { StockPlacementContent } from "../../../../../../api/storePlaces";
import { resolveStockPlacementContentScan } from "../../helpers/resolveStockPlacementContentScan";

const CONTENT_SCAN_HINT: ScannerHintConfig = {
    stateKey: "stock-placement.card.scan-content",
    title: "Сканируйте номер партии",
    description: "Найдём строку состава приходного ордера по номеру партии.",
    position: { placement: "bottom-left" },
    indicator: { variant: "scanner" },
};

/** Пропсы компонента {@link StockPlacementScanPlaceholder}. */
interface StockPlacementScanPlaceholderProps {
    /** Код текущего приходного ордера — для сверки `lot.DocCode`. */
    orderCode: number;
    /** Строки состава приходного ордера, среди которых ищется скан партии. */
    contents: StockPlacementContent[];
    /** Включён ли сценарий сканирования партии. */
    enabled: boolean;
    /**
     * Открывает форму найденной строки состава.
     * @param contentCode код строки состава приходного ордера
     */
    onOpenContent: (contentCode: number) => void;
}

/** Регистрирует сценарий сканирования партии в карточке размещения. */
export const StockPlacementScanPlaceholder: FC<StockPlacementScanPlaceholderProps> = ({
    orderCode,
    contents,
    enabled,
    onOpenContent,
}) => {
    const handleScan = useCallback(
        async (barcode: string): Promise<void> => {
            const result = await resolveStockPlacementContentScan(barcode, contents, orderCode);

            if (result.type === "notFound") {
                notification.warning(`В составе приходного ордера не найдена строка для партии "${barcode}".`);
                return;
            }

            if (result.type === "ambiguous") {
                notification.warning(`Для партии "${barcode}" найдено несколько строк состава. Откройте строку вручную.`);
                return;
            }

            onOpenContent(result.contentCode);
        },
        [contents, onOpenContent, orderCode],
    );

    const scannerRegistration = useMemo(
        (): ScannerRegistrationConfig => ({
            enabled,
            priority: "screen",
            captureInEditable: false,
            hint: CONTENT_SCAN_HINT,
            onScan: handleScan,
            errorSource: "stock.placement.card.scanContent",
        }),
        [enabled, handleScan],
    );

    useScannerRegistration(scannerRegistration);

    return null;
};
